/* eslint-disable no-unused-vars */
import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { axiosBaseQuery } from '../axios/index';
import { campaignReportsDefaultSelectedCols } from 'constants';

function isValidJson(value) {
  if (value === null || value === undefined || value === '') {
    return JSON.stringify(campaignReportsDefaultSelectedCols);
  }
  if (typeof value !== 'string') {
    return JSON.stringify(campaignReportsDefaultSelectedCols);
  }
  try {
    const JSONParseRes = JSON.parse(value);
    const validationRes = validateSelectedCols(JSONParseRes);
    return validationRes;
  } catch (e) {
    return JSON.stringify(campaignReportsDefaultSelectedCols);
  }
}

function isValidObject(item) {
  return (
    item !== null &&
    typeof item === 'object' &&
    'colName' in item &&
    'label' in item &&
    'isSelected' in item &&
    'selectedOrder' in item
  );
}

const validateSelectedCols = (selectedCols) => {
  const { cols, title1, title2 } = selectedCols;
  let validItems = [];

  // remove invalid and cols that are hidden from campaignReportsDefaultSelectedCols
  const filteredServerCols = cols.filter(
    (item) =>
      isValidObject(item) &&
      campaignReportsDefaultSelectedCols.cols.some((col) => col.colName === item.colName),
  );

  if (cols.length !== campaignReportsDefaultSelectedCols.cols.length) {
    const additionalCols = campaignReportsDefaultSelectedCols.cols.filter((col) => {
      return !cols.some((c) => c.colName === col.colName);
    });
    validItems = [...filteredServerCols, ...additionalCols];
  } else {
    validItems = filteredServerCols;
  }

  if (validItems.length > 0) {
    return JSON.stringify({ ...selectedCols, cols: validItems });
  }

  return JSON.stringify(campaignReportsDefaultSelectedCols);
};

export const campaignReportsAPI = createApi({
  reducerPath: 'CampaignReportsAPI',
  baseQuery: axiosBaseQuery({ baseUrl: '/api' }),
  tagTypes: ['Campaign/GetCampaign', 'Campaign/getCampaignReport', 'Campaign/getAllExcludedApps'],
  endpoints: (build) => ({
    GetCampaignReport: build.query({
      query: (queryString) => {
        return {
          url: `/Campaign/getCampaignReport?type=${queryString}`,
          method: 'POST',
        };
      },
      providesTags: ['Campaign/getCampaignReport'],
      transformResponse: (response, meta, arg) => {
        const appsData =
          response?.appsData?.map((item) => {
            item.event_id_arr.forEach((id, ind) => {
              item['events_' + id] = item.eventCountArr[ind];
              item['eventsCohort_' + id] = item.eventCountArrCohort[ind];
            });
            item.AvgCpi = item.Installs ? item.Cost / item.Installs : 0;
            // item.eCPM =
            //   item.Impressions > 100 && !Number.isNaN(+item.Cost / +item.Impressions)
            //     ? (+item.Cost / +item.Impressions) * 1000
            //     : 0;
            return item;
          }) || [];

        const newSelectedCols = isValidJson(response.selectedCols);
        return { ...response, appsData, selectedCols: newSelectedCols };
      },
    }),
    GetCampaignGraphData: build.query({
      query: (data) => {
        return {
          url: `/Campaign/GetCampaignGraphData`,
          method: 'POST',
          data,
        };
      },
      providesTags: ['Campaign/GetCampaignGraphData'],
      // transformResponse: (response, meta, arg) => {
      //   return response;
      // },
    }),
    GetCampaignData: build.query({
      query: (data) => {
        const { cm_id } = data;
        return {
          url: `/Campaign/GetCampaign`,
          method: 'GET',
          //data,
          params: { id: cm_id },
        };
      },
      keepUnusedDataFor: 0,
      providesTags: ['Campaign/GetCampaign'],
      transformResponse: (response, meta, arg) => {
        return response;
      },
    }),
    SaveColumnsOrder: build.mutation({
      query: (data) => ({
        url: '/Campaign/columnsHideSave',
        method: 'POST',
        data,
      }),
    }),
    UpdateCampaignBid: build.mutation({
      query: (data) => ({
        url: '/CampaignsManage/UpdateCampiagnBid',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Campaign/GetCampaign'],
    }),
    UpdateDailyBudget: build.mutation({
      query: (data) => ({
        url: '/CampaignsManage/UpdateDailyBudget',
        method: 'POST',
        data,
      }),
      // The budget is written to the master DB, but GetCampaign reads it back from a
      // read replica. Invalidating here refetched the campaign immediately and often
      // won the race against replication, showing the pre-save budget after a
      // successful save. Patch the cache with the budget the server confirmed instead.
      async onQueryStarted({ campaignId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (!data?.success || data.budget == null) return;
          dispatch(
            campaignReportsAPI.util.updateQueryData(
              'GetCampaignData',
              { cm_id: campaignId },
              (draft) => {
                if (draft) draft.budget = data.budget;
              },
            ),
          );
        } catch {
          // Save failed - the error toast already covers it, leave the cache untouched.
        }
      },
    }),
    GetAllExcludedApps: build.query({
      query: (data) => {
        return { url: '/Campaign/getAllExcludedApps', method: 'POST', data };
      },
      providesTags: ['Campaign/getAllExcludedApps'],
      transformResponse: (response, meta, arg) => {
        response.excludedApps = new Set(response?.excludedApps);
        response.excludedAppsPublisher = new Set(response?.excludedAppsPublisher);
        return response;
      },
    }),
    UpdateCampaignStatus: build.mutation({
      query: (data) => ({
        url: '/CampaignsManage/UpdateCampaignStatus',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Campaign/GetCampaign'],
    }),
    UpdateWebCampaignStatus: build.mutation({
      query: (data) => ({
        url: '/CampaignsManage/UpdateWebCampaignStatus',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Campaign/GetCampaign'],
    }),
    BulkActionsEnableDisableCreative: build.mutation({
      query: (data) => ({
        url: '/Campaign/bulkActionsEnableDisableCreative',
        method: 'POST',
        data,
      }),
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        //invalidateTags only after successful
        try {
          const result = await queryFulfilled;
          console.log(result);
          dispatch(campaignReportsAPI.util.invalidateTags(['Campaign/getCampaignReport']));
        } catch {
          console.log('server error');
        }
      },
    }),
    ResetCustomCpiBulk: build.mutation({
      query: (data) => ({
        url: '/Campaign/resetCustomCpiBulk',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Campaign/getCampaignReport', 'Campaign/getAllExcludedApps'],
    }),
    BulkUpdateCampainAppTargeting: build.mutation({
      query: (data) => ({
        url: '/Campaign/bulkUpdateCampainAppTargeting',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Campaign/getCampaignReport', 'Campaign/getAllExcludedApps'],
    }),
    IncreaseDecreaseBidBulk: build.mutation({
      query: (data) => ({
        url: '/Campaign/increaseDecreaseBidBulk',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Campaign/getCampaignReport', 'Campaign/getAllExcludedApps'],
    }),
    IncreaseDecreaseBid: build.mutation({
      query: (data) => ({
        url: '/Campaign/increaseDecreaseBidBulk',
        method: 'POST',
        data,
      }),
      // invalidatesTags: ['Campaign/getCampaignReport', 'Campaign/getAllExcludedApps'],
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        try {
          const result = await queryFulfilled;
        } catch {
          console.log('server error');
          //invalidateTags only if error
          dispatch(
            campaignReportsAPI.util.invalidateTags([
              'Campaign/getCampaignReport',
              'Campaign/getAllExcludedApps',
            ]),
          );
        }
      },
    }),
    UpdateCampaignAppTargeting: build.mutation({
      query: (data) => ({
        url: '/Campaign/UpdateCampainAppTargeting',
        method: 'POST',
        data,
      }),
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        //invalidateTags only after successful
        try {
          const result = await queryFulfilled;
          console.log(result);
          dispatch(
            campaignReportsAPI.util.invalidateTags([
              'Campaign/getCampaignReport',
              'Campaign/getAllExcludedApps',
            ]),
          );
        } catch {
          console.log('server error');
        }
      },
      //invalidatesTags: ['Campaign/getCampaignReport', 'Campaign/getAllExcludedApps'],
    }),
  }),
});
